<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable, Writable } from 'svelte/store'

    import type { GenOrder, IItem, IPZoneYearPrice, IYearPrice } from '../order'
    import Icon from '@iconify/svelte'
    import { DateTime } from 'luxon'
    import { toUnicode } from 'punycode-esm'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'
    import { escapeHtml } from 'hydria/utils/html'
    import { intToVisual, numberToVisual } from 'hydria/utils/numbers'
    import { toLuxon } from 'hydria/utils/time'

    import Dropdown from 'hydria/components/Dropdown.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import Modal from 'hydria/components/Modal.svelte'
    import IntCurrency from 'wdom/components/IntCurrency.svelte'
    import OrderModalNs from '../OrderModalNs.svelte'
    import OrderModalQcIntendedUse from '../OrderModalQcIntendedUse.svelte'

    const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    const status = sderived(Order.status, ($status) => $status)
    const completion = sderived(Order.completion, ($completion) => $completion)
    const items = sderived(Order.items, ($items) => $items)

    const order = sderived(Order.orderStore, ($order) => $order)
    const existingDomains = sderived(Order.existingDomains, ($v) => $v)
    const existingZones = sderived(Order.existingZones, ($v) => $v)

    // $: showActivation = $status == 'ready' && !['draft', 'ready'].includes($order.status)
    let showActivation = $derived($status == 'ready' && ['unpaid', 'paid'].includes($order.status))
    let hasDomainType = $derived($status == 'ready' && $items.filter((item) => item.type == 'DOMAIN').length > 0)

    interface Props {
        // const selectedItems: Writable<Set<string>> = getContext('selectedCartItems')
        selectedItems: Writable<Set<string>>
    }

    const domainRenewalIdnas = sderived(items, ($items) => {
        const l: string[] = []
        for (const item of $items) {
            if (item.type == 'RENEWAL') {
                l.push(item.meta.a_name)
            }
        }
        return l
    })

    let { selectedItems }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function initSelection($items: IItem[], showActivation: boolean) {
        if (!showActivation) {
            selectedItems.set(new Set())
            return
        }

        const allIds = new Set($items.map((item) => item.id))
        const newSel = new Set([...$selectedItems].filter((x) => allIds.has(x)))

        $selectedItems = newSel
    }
    function handleItemSelection(e: MouseEvent, item: IItem) {
        selectedItems.update(($selectedItems) => {
            if ($selectedItems.has(item.id)) $selectedItems.delete(item.id)
            else $selectedItems.add(item.id)
            return $selectedItems
        })
        setTimeout(() => ((e.target as HTMLInputElement).checked = $selectedItems.has(item.id)), 0)
    }

    let initialized = false
    $effect(() => {
        if (!initialized && $status == 'ready') {
            initialized = true
            initSelection($items, showActivation)
        }
    })

    interface ZoneLimitPeriod {
        n_days: number
        n_real_days: number
        price_per_block: number
        start_date: DateTime
        end_date: DateTime
    }
    interface ZoneLimits {
        current_period: ZoneLimitPeriod
        exp_period: ZoneLimitPeriod
        exp_time: DateTime
        locked_count: number
        now: DateTime
        published_count: number
        total_slots_admin: number
        total_slots_at_exp: number
        total_slots_now: number
        unpublished_count: number
        zone_count: number
    }
    let zoneLimits: ZoneLimits | undefined = $state()
    let zoneLimitsUserId: string

    async function loadZoneLimits() {
        let r: SimpleFetchResult

        if (zoneLimitsUserId == $order.owner) return

        try {
            r = await simpleFetch('GET', `/json/ecom/1/zone-ecom-limits/${$order.owner}`)
        } catch (e) {
            console.error('Failed to load zone limits:', e)
            notify({
                type: 'danger',
                title: 'Limites pour les zones',
                text: 'Les limites de périodes pour les zones en « slot » n’ont pas pu être obtenues.',
            })
            zoneLimits = undefined
            return
        }

        // Convert all dates to Luxon
        for (const key of ['exp_time', 'now']) {
            if (r.data.limits[key] != null) r.data.limits[key] = toLuxon(r.data.limits[key])
        }
        for (const p of [r.data.limits.current_period, r.data.limits.exp_period]) {
            if (p == null) continue
            for (const key of ['start_date', 'end_date']) {
                if (p[key] != null) p[key] = toLuxon(p[key])
            }
        }

        zoneLimitsUserId = $order.owner
        zoneLimits = r.data.limits
    }

    async function loadRenewTags() {
        let r: SimpleFetchResult

        if ($domainRenewalIdnas.length < 1) {
            return {}
        }

        try {
            r = await simpleFetch('POST', `/json/domain/1/domains-tags`, { idna_list: $domainRenewalIdnas })
        } catch (e: any) {
            console.error('Failed to obtain tags for domains in renewal', e)
            return {}
        }

        return r.data.domain_tags
    }

    let renewalTags: { [key: string]: string } = $state({})

    unsubs.push(
        domainRenewalIdnas.subscribe(() => {
            loadRenewTags().then((r) => {
                renewalTags = r
            })
        }),
    )

    // let adminMessageHtml: string = $state('')
    let adminMessageEditValue: string = $state('')
    let editAdminMessage: boolean = $state(false)
    let editAdminMessageElement: HTMLTextAreaElement | undefined = $state()

    unsubs.push(
        order.subscribe(($order) => {
            if ($order) {
                loadZoneLimits()
            }
        }),
    )
    $effect(() => {
        if (editAdminMessageElement != null) editAdminMessageElement.focus()
    })

    async function setAdminMessage(nValue: string) {
        if (!!working) {
            return
        }
        working = 'admin-message'
        editAdminMessage = false

        let r: SimpleFetchResult

        try {
            r = await simpleFetch('PUT', `/json/ecom/1/order/${$orderId}/admin-message`, { text: nValue })
        } catch (e) {
            console.error('Failed to set admin message:', e)
            working = false
            notify({
                type: 'danger',
                title: $_('Message administratig'),
                text: 'N’a pas pu être configuré.',
            })
            return
        }

        Order.loadGenericResponse(r.data)
    }
    function popAdminMessage() {
        adminMessageEditValue = $order.admin_message
        editAdminMessage = true
    }

    function periodText(item: IItem, nZones: number) {
        if (!zoneLimits) return 'is null?'

        // const periodTag = zoneLimits.exp_time == null ? 'exp' : 'current'
        const periodTag = item.id == 'Z-CURRENT' ? 'current' : 'exp'
        const period = zoneLimits[`${periodTag}_period`]
        if (period == null) return 'is null?'

        const price = nZones * period.price_per_block
        let line1 = $ngettext('{n} zone for {price}', '{n} zones for {price}', { n: nZones, price: intToVisual(price) })
        // let selected = $ngettext('{n} zone', '{n} zones', { n: nZones })
        let line2: string
        if (periodTag == 'current') {
            line2 = $ngettext('{n} day from today', '{n} days from today', { n: period.n_real_days })
        } else {
            if (zoneLimits.exp_time != null) {
                line2 = $ngettext('{n} day from {start}', '{n} days from {start}', { n: period.n_real_days, start: zoneLimits.exp_time.toLocaleString(DateTime.DATE_MED) })
            } else {
                line2 = $ngettext('{n} day', '{n} days', { n: period.n_real_days })
            }
        }
        return `<div>${line1}</div><div style="font-size: 11px; color: #666; padding-left: 1em;">${line2}</div>`
    }

    let privaciesInCart: Set<string> = new Set()

    function categorizeItems(items: IItem[]) {
        privaciesInCart.clear()
        for (const item of items) {
            if (item.type == 'PRIVACY') privaciesInCart.add(item.meta.a_name)
        }
        privaciesInCart = privaciesInCart
    }

    interface SItem {
        item: IItem
        year_price?: IYearPrice
        extra_year_list?: IYearPrice[]
        n_slots?: any
    }
    let shadowItems: SItem[] = $state([])
    function setupShadowItems(items: IItem[]) {
        const sitems: SItem[] = []
        for (const item of items) {
            const sitem = {
                item: item,
            }
            switch (item.type) {
                case 'DOMAIN':
                case 'RENEWAL':
                    sitem['year_price'] = item.year_price_list ? item.year_price_list.filter((v) => v.n_years == item.meta.n_years)[0] : null

                    sitem['extra_year_list'] = item.year_price_list ? item.year_price_list.filter((v) => item.meta.n_years_activated == null || v.n_years >= item.meta.n_years_activated) : null
                    break
                case 'PZONE':
                    sitem['year_price'] = item.zone_possibilities ? (item.zone_possibilities as unknown as IPZoneYearPrice[]).filter((v) => v.years == item.meta.n_years)[0] : null
                    break
                case 'ZONES':
                    sitem['n_slots'] = item.meta.n_slots
                    break
            }

            sitems.push(sitem)
        }
        shadowItems = sitems
    }
    let working: false | true | 'intended-use' | 'name-servers' | 'admin-message' = $state(true)
    function doneWorking() {
        if (working) working = false
    }

    unsubs.push(
        items.subscribe(($items) => {
            if ($items) {
                categorizeItems($items)
                setupShadowItems($items)
                doneWorking()
            }
        }),
    )

    function htmlDescription(item: IItem, checkWarning: boolean = false) {
        const type = item.type == 'PZONE' ? 'zone' : ['TRANSFER', 'DOMAIN', 'RENEWAL', 'PRIVACY'].includes(item.type) ? 'domain' : ''

        const parts = [] as string[]

        if (item.editable && !item.finalized && !showActivation && $order.status != 'final' && ['domain', 'zone'].includes(type)) {
            const count = type == 'domain' ? $existingDomains[item.meta.a_name] : $existingZones[item.meta.a_name]
            if (count) {
                if (checkWarning) return true
                const text = $ngettext('Existe dans {n} autre commande ouverte', 'Existe dans {n} autres commandes ouvertes', { n: count })
                parts.push(`<a class="text-danger tt-right oid-warning" data-sgoto="/ecom/orders?q=${item.meta.u_name}" href="/ecom/orders?q=${item.meta.u_name}" data-tooltip="${text}">${iconExclamationTriangle}</a>`)
            }
        }
        if (checkWarning) return false

        let transform: 'domain' | 'zone' | undefined = undefined
        switch (item.type) {
            case 'RENEWAL':
                transform = 'domain'
                break
            case 'DOMAIN':
            case 'TRANSFER':
            case 'PRIVACY':
                if (!item.editable || item.finalized || !showActivation || $order.status == 'final') transform = 'domain'
                break
            case 'PZONE':
                if (!item.editable || item.finalized || !showActivation || $order.status == 'final') transform = 'zone'
                break
            default:
                transform = undefined
                parts.push(escapeHtml(item.desc) + ' ')
                break
        }

        if (transform) {
            let html = item.desc_semi_resolved ? item.desc_semi_resolved : item.desc
            const repls = { ...item.desc_params }

            if (repls) {
                if (transform == 'domain' && repls['domain']) {
                    repls.domain = `<a data-sgoto="/domain/${item.meta.a_name}" href="/domain/${item.meta.a_name}">${repls['domain']}</a>`
                } else if (transform == 'zone' && repls['domain']) {
                    repls.domain = `<a data-sgoto="/zone/${item.meta.a_name}" href="/zone/${item.meta.a_name}">${repls['domain']}</a>`
                }

                for (const [k, v] of Object.entries(repls)) {
                    html = html.replace(`{${k}}`, v)
                }
            }
            parts.push(html + ' ')
        }

        // *needs* no spacing so the alert in a <a..> *has no spacing after*. Add explicit
        // spacing at the end of each "line" above.
        return parts.join('')
    }

    async function updateQuantity(item: IItem, qty: number) {
        if (working) return

        working = true
        let ok: boolean = false
        switch (item.type) {
            case 'RENEWAL':
                ok = await Order.addDomain(item.meta.a_name, qty, { isRenewal: true })
                break
            case 'DOMAIN':
                ok = await Order.addDomain(item.meta.a_name, qty)
                break
            case 'ZONES':
                ok = await Order.addZoneSlots(qty, item.id == 'Z-CURRENT')
                break
            case 'PZONE':
                ok = await Order.addZone(item.meta.a_name, qty)
                break
        }
        if (!ok) working = false
    }

    async function removeItem(item: IItem) {
        if (working) return

        working = true
        let ok: boolean
        ok = await Order.removeItem(item)

        if (!ok) working = false
    }

    let editIntendedUse = $state(false)

    async function setIntendedUse(value: string) {
        working = 'intended-use'
        editIntendedUse = false
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('PUT', `/json/ecom/1/order/${$orderId}/quebec-intended-use`, { text: value })
        } catch (e) {
            console.error('Failed to set intended-use:', e)
            working = false
            notify({
                type: 'danger',
                title: $_('Intended use for .quebec domains'),
                text: 'N’a pas pu être configuré.',
            })
            return
        }

        Order.loadGenericResponse(r.data)
        notify({
            type: 'success',
            title: $_('Intended use for .quebec domains'),
            text: `Changé pour : « ${value} »`,
        })
    }

    let editNameServers = $state(false)

    async function setNameServers(value: string[]) {
        working = 'name-servers'
        editNameServers = false

        let r: SimpleFetchResult

        try {
            r = await simpleFetch('PUT', `/json/ecom/1/order/${$orderId}/nameservers`, { nameservers: value })
        } catch (e) {
            console.error('Failed to set name servers:', e)
            working = false
            notify({
                type: 'danger',
                title: $_('Serveurs DNS'),
                text: 'N’a pas pu être configuré.',
            })
            return
        }

        Order.loadGenericResponse(r.data)
        let nv: string
        if (value.length) nv = `Les serveurs de nom sont maintenant :<br><ul><li>${value.join('</li><li>')}</li></ul>`
        else nv = 'Les serveurs de nom vont maintenant utiliser la <strong>configuration par défaut</strong>.'
        notify({
            type: 'success',
            title: $_('Serveurs DNS'),
            html: nv,
        })
    }
</script>

<style>
    .order-items-heading {
        display: flex;
        gap: 1rem;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        align-items: center;
    }
    .order-items-heading > * {
        flex: 0 1 auto;
    }
    .order-items-heading > *:first-child {
        flex: 1 2 0;
    }

    .oid-summary-row {
        display: flex;
        justify-content: space-between;
        gap: 1em;
    }
    /* .oid-user-instructions,
    .oid-admin-message {
        align-self: end;
    } */
    .with-instructions,
    .with-admin-message {
        white-space: pre-line;
    }
    .no-instructions,
    .no-admin-message {
        font-style: italic;
        opacity: 0.5;
    }
    .oid-summary-wrap {
        flex: 0 1 0;
        display: flex;
        flex-direction: column;
        white-space: nowrap;
    }
    .oid-texts {
        flex: 1 0 0;
        display: flex;
        flex-direction: column;
        gap: 1em;
        justify-content: space-between;
    }
    .oid-am-edit {
        float: right;
    }
    .tag-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5em 0.25em;
    }

    .item-categorization {
        font-size: 0.9em;
        opacity: 0.5;
    }
    .item-cat-label {
        font-style: italic;
    }
</style>

{#if editIntendedUse}
    <OrderModalQcIntendedUse value={$order.meta.quebec_intended_use ? $order.meta.quebec_intended_use : ''} close={() => (editIntendedUse = false)} save={(e) => setIntendedUse(e)} />
{/if}
{#if editNameServers}
    <OrderModalNs value={$order.meta.nameservers ? $order.meta.nameservers : []} close={() => (editNameServers = false)} save={(e) => setNameServers(e)} />
{/if}

<div class="card">
    <div class="card-title text-primary">
        <div class="text">{$_('Order')}</div>
        <div>
            {#if $status == 'ready'}
                {#if $order.status == 'draft' && hasDomainType}
                    {#if working == 'name-servers'}
                        <div class="progress" style="--bs-progress-border-radius: 0.25rem;">
                            <div class="progress-bar progress-bar-striped text-bg-info progress-bar-animated" style="padding: 0.25rem .5rem;">{$_('Serveurs DNS')}</div>
                        </div>
                    {:else}
                        <button type="button" class="btn btn-sm" class:btn-outline-secondary={!$order.meta.nameservers || !$order.meta.nameservers.length} class:btn-success={$order.meta.nameservers && $order.meta.nameservers.length} onclick={() => (editNameServers = true)}>{$_('Serveurs DNS')}</button>
                    {/if}
                {/if}
                {#if $order.status == 'draft' && $completion.has('requires_quebec_intended_use')}
                    {#if working == 'intended-use'}
                        <div class="progress" style="--bs-progress-border-radius: 0.25rem;">
                            <div class="progress-bar progress-bar-striped text-bg-info progress-bar-animated" style="padding: 0.25rem .5rem;">{$_('Intended use (.quebec)')}</div>
                        </div>
                    {:else}
                        <button type="button" class="btn btn-sm" class:btn-warning={!$completion.has('quebec_intended_use')} class:btn-success={$completion.has('quebec_intended_use')} onclick={() => (editIntendedUse = true)} disabled={!!working}>
                            {$_('Intended use (.quebec)')}
                        </button>
                    {/if}
                {/if}
                {#if ['draft', 'quotation'].includes($order.status) && $completion.has('requires_contact')}
                    <a use:sanchor href={spath(`/ecom/order/${$orderId}/contacts`)} class="btn btn-sm" class:btn-warning={!$completion.has('contact')} class:btn-success={$completion.has('contact')}>{$_('Contacts')}</a>
                {/if}
                {#if ['unpaid', 'quotation', 'paid', 'final'].includes($order.status)}
                    <a use:sanchor href={spath(`/ecom/order/${$orderId}/invoice`)} class="btn btn-sm btn-info">{$_('Invoice')}</a>
                {/if}
            {/if}
        </div>
    </div>

    <div class="card-body">
        <div id="order-item-details" class:hide-trash={$status != 'ready' || $order.status == 'final' || $order.status == 'deleted'} class:hide-activation={$status != 'ready' || !showActivation || $order.status == 'final'}>
            <div class="oid-header">
                <div></div>
                <div></div>
                <div></div>
                <div>{$_('Description')}</div>
                <div>{$_('Quantity')}</div>
                <div>{$_('Price')}</div>
                <div></div>
            </div>
            {#if $status != 'ready'}
                <LoadingPattern pattern={['x', 'x', 'x', 'x']} />
            {:else if !$items}
                <div style="padding: 0.5ex 2em; text-align: center;">La commande est vide.</div>
            {:else}
                {#each $items as item, i}
                    <div class="oid-item oid-type-{item.type.toLowerCase()}" class:oid-item-first={i == 0} class:oid-item-last={i == $items.length - 1}>
                        <div>{item.id}</div>
                        <div>
                            {#if showActivation}
                                <input
                                    type="checkbox"
                                    checked={$selectedItems.has(item.id)}
                                    disabled={item.finalized}
                                    onclick={(e) => {
                                        e.preventDefault()
                                        handleItemSelection(e, item)
                                    }}
                                />
                            {/if}
                        </div>
                        <div>
                            {#if showActivation}
                                <Icon icon={item.finalized ? 'fa:check-circle-o' : 'fa:circle-o'} />{#if ['DOMAIN', 'RENEWAL'].includes(item.type)}<span style="margin-left: 1em">{item.meta.n_years_activated ? item.meta.n_years_activated : 0} / {item.meta.n_years}</span>{/if}
                            {/if}
                        </div>
                        <!-- Description ################################################################# -->
                        <div>
                            {#if !showActivation && ['DOMAIN', 'RENEWAL', 'TRANSFER', 'PZONE'].includes(item.type)}
                                <div class:oid-has-alert={htmlDescription(item, true)}>
                                    <div
                                        role="button"
                                        tabindex="0"
                                        onkeypress={(e) => {
                                            const tgt = e.target && (e.target as HTMLAnchorElement).getAttribute('data-sgoto')
                                            if (tgt && [' ', 'Enter'].includes(e.key)) {
                                                e.preventDefault()
                                                sgoto(tgt)
                                            }
                                        }}
                                        onclick={(e) => {
                                            const tgt = e.target && (e.target as HTMLAnchorElement).getAttribute('data-sgoto')
                                            if (tgt) {
                                                e.preventDefault()
                                                sgoto(tgt)
                                            }
                                        }}
                                    >
                                        {@html htmlDescription(item)}
                                    </div>
                                    {#if item.type == 'TRANSFER'}
                                        <div class="oid-auth" class:oid-no-auth-code={!item.meta.auth_code} class:oid-has-auth-code={item.meta.auth_code}><span class="oid-auth-label">Auth code :</span> <span class="oid-auth-code">{item.meta.auth_code ? item.meta.auth_code : 'n/a'}</span></div>
                                        {#if item.meta.extra_info && item.meta.extra_info.length > 0}
                                            <div class="oid-extra-info">
                                                <span class="oid-extra-info-label">Extra infos :</span>
                                                <div class="oid-extra-info-content">{item.meta.extra_info}</div>
                                            </div>
                                        {/if}
                                    {:else if item.type == 'RENEWAL'}
                                        {#if renewalTags[item.meta.a_name]}
                                            <div class="tag-list">
                                                {#each renewalTags[item.meta.a_name] as rt}
                                                    <span class="badge text-bg-secondary">{rt}</span>
                                                {/each}
                                            </div>
                                        {/if}
                                    {/if}
                                </div>
                            {:else if item.type == 'MANUAL'}
                                {item.desc}
                                {#if item.meta.short}<div class="oid-m-listdesc">{item.meta.short}</div>{/if}
                                {#if item.meta.pzone}
                                    <div class="oid-m-zone">
                                        {#if item.meta.pzone.a_name == toUnicode(item.meta.pzone.a_name)}
                                            {$_('– zone “{aname}”, until {date}', { aname: item.meta.pzone.a_name, date: toLuxon(item.meta.pzone.expiration).toFormat('yyyy-MM-dd') })}
                                        {:else}
                                            {$_('– zone “{uname}” ({aname}), until {date}', { aname: item.meta.pzone.a_name, uname: toUnicode(item.meta.pzone.a_name), date: toLuxon(item.meta.pzone.expiration).toFormat('yyyy-MM-dd') })}
                                        {/if}
                                    </div>
                                {/if}
                            {:else if item.type == 'TRANSFER'}
                                {item.desc}
                                {#if item.meta && item.meta.auth_code}
                                    <div class="oid-auth oid-has-auth-code"><span class="oid-auth-label">Auth code :</span> <span class="oid-auth-code">{item.meta.auth_code}</span></div>
                                {/if}
                            {:else}
                                {item.desc}
                            {/if}
                            {#if item.type == 'RENEWAL' && item.meta.user_domain_category != null && item.meta.user_domain_category.id}
                                <div class="item-categorization">
                                    <span class="item-cat-label">Catégorie :</span>
                                    <span class="item-cat-value">{item.meta.user_domain_category.name}</span>
                                </div>
                            {/if}
                        </div>
                        <!-- Quantity #################################################################### -->
                        {#if item.type == 'PZONE'}
                            {#if item.editable && !item.finalized && (!showActivation || !['final', 'paid'].includes($order.status)) && item.zone_possibilities && item.zone_possibilities.length > 1}
                                <div>
                                    <Dropdown compare={(a, b) => a.years == b.years} onSelect={(e) => updateQuantity(item, e.years)} selected={shadowItems[i].year_price} items={item.zone_possibilities} placeholder="" text={(sitem) => $ngettext('{n} year for {price}', '{n} years for {price}', { n: sitem.years, price: intToVisual(sitem.price) })} selectedText={(sitem) => $ngettext('{n} year', '{n} years', { n: sitem.years })} disabled={!!working} inline={true} size="sm" alignRight={true} />
                                </div>
                            {:else}
                                <span>{$ngettext('{n} year', '{n} years', { n: item.meta.n_years })}</span>
                            {/if}
                        {:else if item.type == 'ZONES'}
                            {#if item.editable && !item.finalized && (!showActivation || !['final', 'paid'].includes($order.status)) && zoneLimits != null}
                                <div>
                                    <Dropdown onSelect={(e) => updateQuantity(item, e)} selected={shadowItems[i].n_slots} items={[1, 2, 3, 4, 5]} placeholder="" textIsHtml={true} text={(sitem) => periodText(item, sitem)} selectedText={(sitem) => $ngettext('{n} zone', '{n} zones', { n: sitem })} disabled={!!working} inline={true} size="sm" alignRight={true} />
                                </div>
                            {/if}
                        {:else if item.type == 'DOMAIN' || item.type == 'RENEWAL'}
                            {#if item.editable && !item.finalized && (!showActivation || !['final', 'paid'].includes($order.status)) && item.year_price_list && item.year_price_list.length > 1}
                                <div>
                                    <Dropdown compare={(a, b) => a.n_years == b.n_years} onSelect={(e) => updateQuantity(item, e.n_years)} selected={shadowItems[i].year_price} items={item.year_price_list} placeholder="" text={(sitem) => $ngettext('{n} year for {price}', '{n} years for {price}', { n: sitem.n_years, price: intToVisual(sitem.price) })} selectedText={(sitem) => $ngettext('{n} year', '{n} years', { n: sitem.n_years })} disabled={!!working} inline={true} size="sm" alignRight={true} />
                                </div>
                            {:else if item.editable && !item.finalized && (!showActivation || !['final', 'paid'].includes($order.status)) && item.year_price_list && item.year_price_list.length > 1 && shadowItems[i].extra_year_list != null && shadowItems[i].extra_year_list.length > 0}
                                <div>
                                    <Dropdown compare={(a, b) => a.n_years == b.n_years} onSelect={(e) => updateQuantity(item, e.n_years)} selected={shadowItems[i].year_price} items={shadowItems[i].extra_year_list} placeholder="" text={(sitem) => $ngettext('{n} year for {price}', '{n} years for {price}', { n: sitem.n_years, price: intToVisual(sitem.price) })} selectedText={(sitem) => $ngettext('{n} year', '{n} years', { n: sitem.n_years })} disabled={!!working} inline={true} size="sm" alignRight={true} />
                                </div>
                            {:else}
                                <span>{$ngettext('{n} year', '{n} years', { n: item.meta.n_years })}</span>
                            {/if}
                        {:else if item.type == 'PRIVACY'}
                            <span>{$ngettext('{n} year', '{n} years', { n: item.qty })}</span>
                        {:else}
                            <span>{item.qty}</span>
                        {/if}
                        <!-- Amount ###################################################################### -->
                        <div>
                            <IntCurrency value={item.value} />
                        </div>
                        <div>
                            {#if item.editable && !item.finalized && (!showActivation || !['final', 'paid'].includes($order.status))}
                                <button type="button" class="btn btn-outline-secondary btn-sm" disabled={!!working} onclick={() => removeItem(item)}><Icon inline icon="fa:trash-o" /></button>
                            {/if}
                        </div>
                    </div>
                {/each}
            {/if}
            {#if $status == 'ready'}
                <div class="oid-summary-row">
                    <div class="oid-texts">
                        {#if $order.user_instructions != null && $order.user_instructions.trim().length > 0}
                            <div class="oid-user-instructions with-instructions">
                                <div class="text-info">{$order.user_instructions.trim()}</div>
                            </div>
                        {:else}
                            <div class="oid-user-instructions no-instructions">
                                <div class="text-muted">(sans instructions spéciales)</div>
                            </div>
                        {/if}
                        <div class="oid-admin-message" class:with-admin-message={!!$order.admin_message} class:no-admin-message={!$order.admin_message}>
                            {#if $order.status != 'final'}
                                <div class="oid-am-edit">
                                    <button
                                        class="btn btn-sm btn-outline-primary"
                                        disabled={!!working}
                                        onclick={() => {
                                            popAdminMessage()
                                        }}><Icon inline icon="mdi:edit" /></button
                                    >
                                </div>
                            {/if}
                            {#if $order.admin_message}
                                <div class="text-success">
                                    {$order.admin_message}
                                </div>
                            {:else}
                                <div class="text-muted">(sans message administratif)</div>
                            {/if}
                        </div>
                    </div>
                    <div class="oid-summary-wrap">
                        {#if $order.taxes.length}
                            <div class="oid-summary oid-subtotal">
                                <div><span>{$_('Subtotal')}</span></div>
                                <div><span><IntCurrency value={$order.subtotal} /></span></div>
                            </div>
                            {#each $order.taxes as tax}
                                <div class="oid-summary oid-tax tax-{tax.name.toLowerCase().replace(/[^a-z0-9]/gi, '-')}">
                                    <div><span>{tax.name} ({numberToVisual(tax.pct, { units: '%', nDecimals: 3 })})</span></div>
                                    <div><span><IntCurrency value={tax.value} /></span></div>
                                </div>
                            {/each}
                        {/if}
                        <div class="oid-summary oid-total">
                            <div><span>{$_('Total')}</span></div>
                            <div><span><IntCurrency value={$order.total} /></span></div>
                        </div>
                        {#if $order.final_transaction}
                            <div class="oid-summary oid-paid">
                                <div><span>{$_('Amount paid')}</span></div>
                                <div><span><IntCurrency forceNeg={true} zeroIsNeg={true} value={$order.status == 'paid' || $order.status == 'final' ? $order.total : 0} /></span></div>
                            </div>
                            <div class="oid-summary oid-due">
                                <div><span>{$_('Amount due')}</span></div>
                                <div><span><IntCurrency value={$order.status == 'paid' || $order.status == 'final' ? 0 : $order.total} /></span></div>
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>

{#if editAdminMessage}
    <Modal close={() => (editAdminMessage = false)}>
        <div class="modal-header text-bg-primary">{$_('Message administratif')}</div>
        <div class="modal-body">
            <textarea id="oid-admin-message-ta" class="form-control" rows="3" bind:this={editAdminMessageElement} bind:value={adminMessageEditValue}></textarea>
        </div>
        <div class="modal-footer d-flex gap-2 justify-content-end">
            <button type="button" class="btn btn-sm btn-secondary" onclick={() => (editAdminMessage = false)}>{$_('Fermer')}</button>
            <button type="button" class="btn btn-sm btn-primary" disabled={adminMessageEditValue == $order.admin_message || !!working} onclick={() => setAdminMessage(adminMessageEditValue)}>{$_('Appliquer')}</button>
        </div>
    </Modal>
{/if}
